import React, { useEffect, useRef } from "react";
import { TypeAnimation } from "react-type-animation";
import { ArrowRight } from "lucide-react";
import styles, { elements } from "../../pages/styles";
import {
  logowithclouds,
  starsalone,
  teacherzoomnew,
  textlogo,
  TrialBackground,
  TrialForeground
} from "../../assets/images";

const NewFreeTrialHero = ({ title, desc, img, bgcolor }) => {
  const backgroundRef = useRef(null);
  const foregroundRef = useRef(null);
  const zoomRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY;
      
      if (backgroundRef.current) {
        // Background does not move
        backgroundRef.current.style.transform = `translateY(0px)`;
      }
      
      if (foregroundRef.current) {
        // Foreground moves down faster
        foregroundRef.current.style.transform = `translateY(${scrolled * 0.5}px)`;
      }
      
      if (zoomRef.current) {
        // Zoom image moves at medium speed (more noticeable)
        zoomRef.current.style.transform = `translateY(${scrolled * 0.15}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="flex flex-col-reverse sm:flex-row items-stretch w-full rounded-t-xl relative -mt-28 md:-mt-44 mb-0 z-0">
      {/* LEFT SECTION (Hero Images) */}
      <div className="relative w-full sm:w-11/12 overflow-hidden">
        {/* This container has a responsive height: 500px on mobile and 740px on desktop */}
        <div className="relative overflow-hidden sm:h-[500px] h-[350px] md:h-[740px]">
          <div
            ref={backgroundRef}
            style={{
              width: "120%",
              height: "120%",
              position: "absolute",
              top: "-20%",
              left: "-10%",
              willChange: "transform"
            }}
          >
            <img
              src={TrialBackground}
              alt="Free Trial Hero Background"
              className="w-full h-full object-cover opacity-80"
            />
          </div>
          {/* Foreground image with parallax */}
          <img
            ref={foregroundRef}
            src={TrialForeground}
            alt="Free Trial Hero Foreground"
            className="absolute bottom-0 left-0 w-full object-cover"
            style={{ willChange: "transform" }}
          />
        </div>
        {/* Teacher Zoom image: Absolutely positioned with parallax */}
        <img
          ref={zoomRef}
          src={teacherzoomnew}
          alt="Teacher Zoom"
          className="zoomImgNew md:w-52 w-36"
          style={{ willChange: "transform" }}
        />
      </div>

      {/* RIGHT SECTION (Logos, Heading, CTA) */}
      <div className="w-full md:px-12 px-6 py-8 md:py-12 gap-4 flex flex-col items-center md:items-start justify-center text-center md:text-left z-10 relative">
        <div className="mb-2">
          <img
            src={textlogo}
            alt="Tech Tails Logo"
            className="w-[140px]"
          />
        </div>
        
        <h1 className={`${styles.biggerheadingdark} leading-tight mb-4`}>
          Unlock Your Child's
          <br className="hidden sm:inline" />
          <span className="relative inline-block">
            Future
            <span className="absolute shadow-sm bottom-0 left-1 w-full h-2 bg-pinkprimary/70 -z-10 -skew-x-[30deg] rounded-sm"></span>
          </span>
          <br className="sm:hidden" />
          {" "}In{" "}
          <TypeAnimation
            repeat={Infinity}
            speed={5}
            sequence={[
              "Technology",
              2000,
              "Programming",
              2000,
              "Creativity",
              2000,
              "Game Design",
              2000,
              "Digital Art",
              2000,
              "Coding",
              1500,
              "3D Modeling",
              2000,
            ]}
          />
        </h1>
        
        <p className={`${styles.paragraph} mb-5 max-w-md leading-relaxed`}>
          Live 1-on-1 instruction tailored to your child's interests. 
          Learn coding, game design, and more with expert teachers.
        </p>
        
        <a
          className={`${elements.bluebutton} flex items-center justify-center gap-2 border-b-[5px] border-orangeprimary shadow-lg hover:shadow-xl transition-shadow w-full md:w-auto`}
          href="#trialClass"
        >
          Get Free Trial Class
          <ArrowRight size={18} />
        </a>
      </div>
    </div>
  );
};

export default NewFreeTrialHero;
